import { API } from '../lib/api';
import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Download, 
  Eye, 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Clock,
  Image as ImageIcon,
  FileText,
  Sparkles,
  Users,
  RotateCcw,
  Settings,
  Upload as UploadIcon,
  MapPin,
  Brain,
  Calendar,
  Target
} from 'lucide-react';
import { ImageUpload } from '../components/ImageUpload';

interface Page {
  id: string;
  pageNumber: number;
  textContent: string;
  imageUrl?: string;
  imagePrompt?: string;
  isValidated: boolean;
}

interface AICharacter {
  name: string;
  description: string;
  role: 'principal' | 'secondaire';
  keyTraits?: string[];
  colorPalette?: string[];
}

interface AIPlace {
  name: string;
  description: string;
  importance: 'principal' | 'secondaire';
  ambiance?: string;
  colorPalette?: string[];
}

interface Project {
  id: string;
  title: string;
  status: string;
  globalStyle?: string;
  characterDescriptions?: string;
  // Nouvelles propriétés pour les descriptions IA
  aiGeneratedCharacters?: string; // JSON string
  aiGeneratedPlaces?: string; // JSON string
  aiGeneratedStyle?: string;
  useAiDescriptions?: boolean;
  aiDescriptionsGeneratedAt?: string;
  createdAt: string;
  updatedAt: string;
  pages: Page[];
}

export const ProjectViewPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPage, setSelectedPage] = useState<number>(1);
  
  // États pour la régénération par page
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [regeneratePageNumber, setRegeneratePageNumber] = useState<number | null>(null);
  const [newGlobalStyle, setNewGlobalStyle] = useState('');
  const [customPrompt, setCustomPrompt] = useState('');
  const [showPromptPreview, setShowPromptPreview] = useState(false);
  const [previewPrompt, setPreviewPrompt] = useState('');
  const [newCharacters, setNewCharacters] = useState<Array<{name: string, description: string}>>([]);
  const [openaiApiKey, setOpenaiApiKey] = useState('');
  const [stopping, setStopping] = useState(false);
  
  // États pour l'ancien système de régénération (temporaire)
  const [regenerateAll, setRegenerateAll] = useState(false);
  const [selectedPages, setSelectedPages] = useState<number[]>([]);
  
  // États pour l'export PDF
  const [pdfExporting, setPdfExporting] = useState(false);
  const [pdfStatus, setPdfStatus] = useState<string | null>(null);
  
  // États pour l'upload d'images
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadPageNumber, setUploadPageNumber] = useState<number | null>(null);

  const fetchProject = async () => {
    if (!projectId) return;
    
    try {
      setLoading(true);
      const response = await fetch(`${API}/api/projects/${projectId}`);
      
      if (!response.ok) {
        throw new Error('Projet introuvable');
      }
      
      const data = await response.json();
      setProject(data.project);
      setError(null);
    } catch (error) {
      console.error('Erreur récupération projet:', error);
      setError(error instanceof Error ? error.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  // Effect initial pour charger le projet
  useEffect(() => {
    fetchProject();
  }, [projectId]);

  // Effect séparé pour le polling automatique
  useEffect(() => {
    if (!project) return;

    const interval = setInterval(() => {
      if (project.status === 'GENERATING') {
        fetchProject();
      }
    }, 5000); // Actualise toutes les 5 secondes

    return () => clearInterval(interval);
  }, [project?.status]);

  // Effect pour maintenir une sélection de page valide
  useEffect(() => {
    if (project && project.pages.length > 0) {
      // Vérifier si la page sélectionnée existe toujours
      const pageExists = project.pages.some(p => p.pageNumber === selectedPage);
      if (!pageExists) {
        // Si la page sélectionnée n'existe plus, sélectionner la première page
        setSelectedPage(project.pages[0].pageNumber);
      }
    }
  }, [project?.pages, selectedPage]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'COMPLETED':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'EXPORTED':
        return <Download className="h-5 w-5 text-purple-500" />;
      case 'GENERATING':
        return <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />;
      case 'DRAFT':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      default:
        return <XCircle className="h-5 w-5 text-red-500" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'COMPLETED': return 'Terminé';
      case 'EXPORTED': return 'Exporté';
      case 'GENERATING': return 'En cours de génération...';
      case 'DRAFT': return 'Brouillon';
      case 'READY_FOR_GENERATION': return 'Prêt pour génération';
      default: return status;
    }
  };

  const parseCharacters = (characterDescriptions?: string) => {
    if (!characterDescriptions) return [];
    try {
      return JSON.parse(characterDescriptions);
    } catch {
      return [];
    }
  };
  
  // Fonctions utilitaires pour parser les données IA
  const parseAICharacters = (aiCharacters?: string): AICharacter[] => {
    if (!aiCharacters) return [];
    try {
      return JSON.parse(aiCharacters);
    } catch {
      return [];
    }
  };
  
  const parseAIPlaces = (aiPlaces?: string): AIPlace[] => {
    if (!aiPlaces) return [];
    try {
      return JSON.parse(aiPlaces);
    } catch {
      return [];
    }
  };
  
  // Fonctions pour la régénération par page
  const handleOpenRegeneratePage = (pageNumber: number) => {
    if (!project) return;
    
    const page = project.pages.find(p => p.pageNumber === pageNumber);
    if (!page) return;
    
    setRegeneratePageNumber(pageNumber);
    // Pré-remplir les champs avec les valeurs actuelles
    setNewGlobalStyle(project.globalStyle || '');
    setCustomPrompt(page.imagePrompt || '');
    setNewCharacters([]);
    setOpenaiApiKey('');
    setShowRegenerateModal(true);
  };
  
  const handleRegeneratePage = async () => {
    if (!projectId || !project || !regeneratePageNumber) return;
    
    try {
      setRegenerating(true);
      
      const options: any = {
        pageNumbers: [regeneratePageNumber]
      };
      
      if (newGlobalStyle.trim()) {
        options.newGlobalStyle = newGlobalStyle.trim();
      }
      
      if (newCharacters.length > 0) {
        options.newCharacters = newCharacters;
      }
      
      if (openaiApiKey.trim()) {
        options.openaiApiKey = openaiApiKey.trim();
      }
      
      if (customPrompt.trim()) {
        options.customPrompt = customPrompt.trim();
      }

      const response = await fetch(`${API}/api/projects/${projectId}/regenerate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(options)
      });

      const result = await response.json();

      if (response.ok) {
        alert(`Régénération démarrée pour la page ${regeneratePageNumber}! Temps estimé: ${result.estimatedTime}`);
        setShowRegenerateModal(false);
        setRegeneratePageNumber(null);
        if (project) {
          setProject(prev => prev ? { ...prev, status: 'GENERATING' } : null);
        }
        // Actualiser le projet après un délai
        setTimeout(fetchProject, 2000);
      } else {
        throw new Error(result.error || 'Erreur lors de la régénération');
      }
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
    } finally {
      setRegenerating(false);
    }
  };
  
  // Fonction pour arrêter la génération
  const handleStopGeneration = async () => {
    if (!projectId || !project) return;
    
    try {
      setStopping(true);
      
      const response = await fetch(`${API}/api/projects/${projectId}/stop-generation`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (response.ok) {
        alert('Arrêt de la génération demandé.');
        // Actualiser le projet pour voir le changement de statut
        setTimeout(fetchProject, 1000);
      } else {
        throw new Error(result.error || 'Erreur lors de l\'arrêt');
      }
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
    } finally {
      setStopping(false);
    }
  };
  
  const addNewCharacter = () => {
    setNewCharacters([...newCharacters, { name: '', description: '' }]);
  };
  
  const updateCharacter = (index: number, field: 'name' | 'description', value: string) => {
    const updated = [...newCharacters];
    updated[index][field] = value;
    setNewCharacters(updated);
  };
  
  const removeCharacter = (index: number) => {
    setNewCharacters(newCharacters.filter((_, i) => i !== index));
  };
  
  // Fonction pour afficher l'aperçu du prompt utilisé initialement
  const generatePromptPreview = () => {
    if (!project) return;
    
    // Utiliser la page actuellement sélectionnée ou la première si aucune sélection
    const currentPage = project.pages.find(p => p.pageNumber === selectedPage) || project.pages[0];
    
    if (currentPage?.imagePrompt) {
      // Si la page a un prompt existant, l'afficher
      setPreviewPrompt(currentPage.imagePrompt);
      setShowPromptPreview(true);
    } else {
      // Si aucun prompt n'existe pour cette page, afficher un message
      alert('Aucun prompt disponible pour cette page. L\'image n\'a pas encore été générée.');
    }
  };
  
  const togglePageSelection = (pageNumber: number) => {
    if (selectedPages.includes(pageNumber)) {
      setSelectedPages(selectedPages.filter(p => p !== pageNumber));
    } else {
      setSelectedPages([...selectedPages, pageNumber]);
    }
  };
  
  // Fonctions pour l'export PDF
  const handleExportPDF = async () => {
    if (!projectId || !project) return;
    
    try {
      setPdfExporting(true);
      setPdfStatus('Génération du PDF en cours...');
      
      const response = await fetch(`${API}/api/pdf/generate/${projectId}`, {
        method: 'POST'
      });
      
      const result = await response.json();
      
      if (response.ok) {
        setPdfStatus('PDF généré avec succès!');
        // Déclencher le téléchargement automatique
        downloadPDF();
        // Mettre à jour le statut du projet
        if (project) {
          setProject(prev => prev ? { ...prev, status: 'EXPORTED' } : null);
        }
      } else {
        throw new Error(result.error || 'Erreur lors de la génération du PDF');
      }
    } catch (err) {
      setPdfStatus(`Erreur: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
    } finally {
      setPdfExporting(false);
      // Effacer le message après 5 secondes
      setTimeout(() => setPdfStatus(null), 5000);
    }
  };
  
  const downloadPDF = () => {
    if (!projectId || !project) return;
    
    const link = document.createElement('a');
    link.href = `${API}/api/pdf/download/${projectId}`;
    link.download = `${project.title}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // Fonctions pour l'upload d'images
  const handleOpenUpload = (pageNumber: number) => {
    setUploadPageNumber(pageNumber);
    setShowUploadModal(true);
  };
  
  const handleUploadSuccess = (imageUrl: string) => {
    // Mettre à jour le projet avec la nouvelle image
    if (project && uploadPageNumber) {
      const updatedProject = {
        ...project,
        pages: project.pages.map(page => 
          page.pageNumber === uploadPageNumber 
            ? { ...page, imageUrl, imagePrompt: 'Image uploadée manuellement' }
            : page
        )
      };
      setProject(updatedProject);
    }
    
    // Fermer le modal après un délai
    setTimeout(() => {
      setShowUploadModal(false);
      setUploadPageNumber(null);
    }, 2000);
  };
  
  const handleUploadError = (error: string) => {
    console.error('Erreur upload:', error);
    // L'erreur est déjà affichée par le composant ImageUpload
  };

  if (loading) {
    return (
      <div className="main-content">
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-magical-purple mr-3" />
          <span className="text-gray-600 font-children">Chargement du projet...</span>
        </div>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="main-content">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-800 mb-2">Erreur</h2>
          <p className="text-red-700">{error || 'Projet introuvable'}</p>
          <Link to="/dashboard" className="btn btn-secondary mt-4 inline-flex items-center">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour au tableau de bord
          </Link>
        </div>
      </div>
    );
  }

  const characters = parseCharacters(project.characterDescriptions);
  const aiCharacters = parseAICharacters(project.aiGeneratedCharacters);
  const aiPlaces = parseAIPlaces(project.aiGeneratedPlaces);
  const selectedPageData = project.pages.find(p => p.pageNumber === selectedPage);
  const completedPages = project.pages.filter(p => p.imageUrl).length;

  return (
    <div className="main-content max-w-7xl mx-auto">
      {/* En-tête */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700 mr-4">
            <ArrowLeft className="h-6 w-6" />
          </Link>
          <div>
            <h1 className="text-4xl font-elegant text-gradient mb-2">
              {project.title}
            </h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                {getStatusIcon(project.status)}
                <span className="ml-2 font-children text-gray-600">
                  {getStatusText(project.status)}
                </span>
              </div>
              <div className="flex items-center text-gray-500">
                <ImageIcon className="h-4 w-4 mr-1" />
                <span className="font-children text-sm">
                  {completedPages}/{project.pages.length} images
                </span>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex space-x-3">
          <button className="btn btn-secondary" onClick={fetchProject}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </button>
          {project.status === 'GENERATING' && (
            <button 
              className="btn btn-warning"
              onClick={handleStopGeneration}
              disabled={stopping}
              title="Arrêter toutes les générations en cours"
            >
              <XCircle className="h-4 w-4 mr-2" />
              {stopping ? 'Arrêt en cours...' : 'Arrêter génération'}
            </button>
          )}
          {(project.status === 'COMPLETED' || project.status === 'EXPORTED') && (
            <div className="flex flex-col items-end gap-2">
              <button 
                onClick={handleExportPDF}
                disabled={pdfExporting}
                className="btn btn-primary disabled:bg-gray-400"
              >
                <Download className="h-4 w-4 mr-2" />
                {pdfExporting 
                  ? 'Export en cours...' 
                  : (project.status === 'EXPORTED' ? 'Ré-exporter PDF' : 'Exporter PDF')
                }
              </button>
              {pdfStatus && (
                <span className="text-xs text-gray-600 font-children">
                  {pdfStatus}
                </span>
              )}
            </div>
          )}
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Panneau de gauche - Informations du projet */}
        
        <div className="lg:col-span-1 space-y-6">
          {/* Style global */}
          {project.globalStyle && (
            <div className="card">
              <h3 className="text-lg font-elegant font-semibold mb-3 flex items-center">
                <Sparkles className="h-5 w-5 mr-2 text-magical-purple" />
                Style artistique
              </h3>
              <p className="font-children text-gray-700 text-sm leading-relaxed">
                {project.globalStyle}
              </p>
            </div>
          )}

          {/* Personnages manuels */}
          {characters.length > 0 && (
            <div className="card">
              <h3 className="text-lg font-elegant font-semibold mb-3 flex items-center">
                <Users className="h-5 w-5 mr-2 text-magical-pink" />
                Personnages manuels ({characters.length})
              </h3>
              <div className="space-y-3">
                {characters.map((character: any, index: number) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-3">
                    <h4 className="font-children font-semibold text-gray-800 text-sm">
                      {character.name}
                    </h4>
                    <p className="font-children text-gray-600 text-xs mt-1">
                      {character.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
                    {/* Sélecteur de pages */}
          <div className="card">
            <h3 className="text-lg font-elegant font-semibold mb-3 flex items-center">
              <FileText className="h-5 w-5 mr-2 text-magical-blue" />
              Pages ({project.pages.length})
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {project.pages.map((page) => (
                <button
                  key={page.id}
                  onClick={() => setSelectedPage(page.pageNumber)}
                  className={`relative p-3 rounded-lg border-2 transition-all ${
                    selectedPage === page.pageNumber
                      ? 'border-magical-purple bg-purple-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="text-center">
                    <div className="text-sm font-children font-semibold">
                      Page {page.pageNumber}
                    </div>
                    <div className="mt-1">
                      {page.imageUrl ? (
                        <CheckCircle className="h-4 w-4 text-green-500 mx-auto" />
                      ) : (
                        <Clock className="h-4 w-4 text-gray-400 mx-auto" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          {/* Personnages générés par IA */}
          {aiCharacters.length > 0 && project.useAiDescriptions && (
            <div className="card border-l-4 border-emerald-400">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-elegant font-semibold flex items-center">
                  <Brain className="h-5 w-5 mr-2 text-emerald-600" />
                  Personnages IA ({aiCharacters.length})
                </h3>
                {project.aiDescriptionsGeneratedAt && (
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(project.aiDescriptionsGeneratedAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {aiCharacters.map((character, index) => (
                  <div key={index} className="bg-emerald-50 rounded-lg p-3 border border-emerald-100">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-children font-semibold text-emerald-800 text-sm">
                        {character.name}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        character.role === 'principal' 
                          ? 'bg-emerald-200 text-emerald-800' 
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        <Target className={`h-2 w-2 inline mr-1 ${
                          character.role === 'principal' ? 'text-emerald-600' : 'text-gray-500'
                        }`} />
                        {character.role}
                      </span>
                    </div>
                    <p className="font-children text-emerald-700 text-xs mt-1 leading-relaxed">
                      {character.description}
                    </p>
                    {character.keyTraits && character.keyTraits.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {character.keyTraits.slice(0, 3).map((trait, traitIndex) => (
                          <span key={traitIndex} className="px-1.5 py-0.5 bg-emerald-100 text-emerald-600 text-xs rounded">
                            {trait}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-emerald-600 font-children flex items-center">
                <Brain className="h-3 w-3 mr-1" />
                Descriptions générées automatiquement par l'IA
              </div>
            </div>
          )}

          {/* Lieux générés par IA */}
          {aiPlaces.length > 0 && project.useAiDescriptions && (
            <div className="card border-l-4 border-blue-400">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-elegant font-semibold flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-blue-600" />
                  Lieux IA ({aiPlaces.length})
                </h3>
                {project.aiDescriptionsGeneratedAt && (
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(project.aiDescriptionsGeneratedAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {aiPlaces.map((place, index) => (
                  <div key={index} className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                    <div className="flex items-center justify-between mb-1">
                      <h4 className="font-children font-semibold text-blue-800 text-sm">
                        {place.name}
                      </h4>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        place.importance === 'principal' 
                          ? 'bg-blue-200 text-blue-800' 
                          : 'bg-gray-200 text-gray-700'
                      }`}>
                        <Target className={`h-2 w-2 inline mr-1 ${
                          place.importance === 'principal' ? 'text-blue-600' : 'text-gray-500'
                        }`} />
                        {place.importance}
                      </span>
                    </div>
                    <p className="font-children text-blue-700 text-xs mt-1 leading-relaxed">
                      {place.description}
                    </p>
                    {place.ambiance && (
                      <div className="mt-2">
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-600 text-xs rounded">
                          Ambiance: {place.ambiance}
                        </span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-3 text-xs text-blue-600 font-children flex items-center">
                <Brain className="h-3 w-3 mr-1" />
                Descriptions générées automatiquement par l'IA
              </div>
            </div>
          )}

          {/* Style IA généré */}
          {project.aiGeneratedStyle && project.useAiDescriptions && (
            <div className="card border-l-4 border-purple-400">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-elegant font-semibold flex items-center">
                  <Sparkles className="h-5 w-5 mr-2 text-purple-600" />
                  Style IA
                </h3>
                {project.aiDescriptionsGeneratedAt && (
                  <div className="flex items-center text-xs text-gray-500">
                    <Calendar className="h-3 w-3 mr-1" />
                    {new Date(project.aiDescriptionsGeneratedAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
              </div>
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <p className="font-children text-purple-700 text-sm leading-relaxed">
                  {project.aiGeneratedStyle}
                </p>
              </div>
              <div className="mt-3 text-xs text-purple-600 font-children flex items-center">
                <Brain className="h-3 w-3 mr-1" />
                Style artistique suggéré par l'IA
              </div>
            </div>
          )}


        </div>

        {/* Panneau de droite - Contenu de la page sélectionnée */}
        <div className="lg:col-span-2">
          {selectedPageData && (
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-elegant font-semibold">
                  Page {selectedPageData.pageNumber}
                </h2>
                <div className="flex items-center space-x-3">
                  {selectedPageData.imageUrl ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Image générée
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                      <Clock className="h-3 w-3 mr-1" />
                      En attente
                    </span>
                  )}
                  
                  {/* Bouton de régénération individuelle */}
                  <button
                    onClick={() => handleOpenRegeneratePage(selectedPageData.pageNumber)}
                    className="btn btn-outline btn-sm"
                    disabled={regenerating || project.status === 'GENERATING'}
                    title="Régénérer cette page uniquement"
                  >
                    <RotateCcw className="h-4 w-4 mr-1" />
                    Régénérer
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Texte */}
                <div>
                  <h3 className="text-lg font-children font-semibold mb-3 text-gray-800">
                    Texte de l'histoire
                  </h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="font-children text-gray-700 leading-relaxed">
                      {selectedPageData.textContent}
                    </p>
                  </div>

                  {/* Prompt généré */}
                  {selectedPageData.imagePrompt && (
                    <div className="mt-4">
                      <h4 className="text-sm font-children font-semibold mb-2 text-gray-700">
                        Prompt pour l'IA
                      </h4>
                      <div className="bg-blue-50 rounded-lg p-3">
                        <p className="font-mono text-xs text-gray-600 leading-relaxed">
                          {selectedPageData.imagePrompt}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Image générée */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-lg font-children font-semibold text-gray-800">
                      Illustration
                    </h3>
                    <button
                      onClick={() => handleOpenUpload(selectedPageData.pageNumber)}
                      className="btn btn-outline btn-sm"
                      disabled={project.status === 'GENERATING'}
                      title="Uploader une image personnalisée"
                    >
                      <UploadIcon className="h-4 w-4 mr-1" />
                      Upload
                    </button>
                  </div>
                  <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden relative group">
                    {selectedPageData.imageUrl ? (
                      <>
                        <img 
                          src={selectedPageData.imageUrl} 
                          alt={`Illustration page ${selectedPageData.pageNumber}`}
                          className="w-full h-full object-cover hover:scale-105 transition-transform cursor-pointer"
                          onClick={() => window.open(selectedPageData.imageUrl, '_blank')}
                        />
                        {/* Overlay avec bouton de remplacement */}
                        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-200 flex items-center justify-center">
                          <button
                            onClick={() => handleOpenUpload(selectedPageData.pageNumber)}
                            className="btn btn-secondary opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                            disabled={project.status === 'GENERATING'}
                          >
                            <UploadIcon className="h-4 w-4 mr-2" />
                            Remplacer
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-gray-400">
                        <div className="text-center">
                          <ImageIcon className="h-16 w-16 mx-auto mb-2 opacity-50" />
                          <p className="font-children text-sm mb-4">
                            {project.status === 'GENERATING' ? 'Génération en cours...' : 'Aucune image'}
                          </p>
                          {project.status !== 'GENERATING' && (
                            <button
                              onClick={() => handleOpenUpload(selectedPageData.pageNumber)}
                              className="btn btn-primary btn-sm"
                            >
                              <UploadIcon className="h-4 w-4 mr-2" />
                              Uploader une image
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Indication si image uploadée manuellement */}
                  {selectedPageData.imageUrl && selectedPageData.imagePrompt === 'Image uploadée manuellement' && (
                    <div className="mt-2 text-xs text-blue-600 font-children flex items-center">
                      <UploadIcon className="h-3 w-3 mr-1" />
                      Image uploadée manuellement
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Barre de progression */}
      {project.status === 'GENERATING' && (
        <div className="fixed bottom-4 right-4 bg-white shadow-lg rounded-lg p-4 border">
          <div className="flex items-center space-x-3">
            <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
            <div>
              <p className="font-children font-semibold text-sm text-gray-800">
                Génération en cours...
              </p>
              <p className="font-children text-xs text-gray-600">
                {completedPages}/{project.pages.length} images générées
              </p>
            </div>
          </div>
          <div className="mt-2 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-magical-purple rounded-full h-2 transition-all duration-300"
              style={{ width: `${(completedPages / project.pages.length) * 100}%` }}
            ></div>
          </div>
        </div>
      )}
      
      {/* Modal de régénération */}
      {showRegenerateModal && regeneratePageNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-elegant font-bold mb-4 text-gradient">
              Régénérer la page {regeneratePageNumber}
            </h2>
            
            {/* Avertissement si génération en cours */}
            {project.status === 'GENERATING' && (
              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-orange-800 font-children">
                      Génération en cours
                    </h3>
                    <div className="mt-2 text-sm text-orange-700 font-children">
                      <p>
                        Une génération est déjà en cours. Cette régénération va <strong>interrompre la génération actuelle</strong> et traiter uniquement cette page.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Information sur la page à régénérer */}
            <div className="mb-6 bg-blue-50 rounded-lg p-4">
              <h3 className="font-children font-semibold text-blue-800 mb-2">
                Page à régénérer : Page {regeneratePageNumber}
              </h3>
              <p className="text-sm text-blue-700 font-children">
                Cette action génèrera une nouvelle image uniquement pour cette page en utilisant les options ci-dessous.
              </p>
            </div>

            {/* Nouveau style artistique */}
            <div className="mb-6">
              <label className="block font-children font-semibold mb-2">
                Nouveau style artistique (optionnel):
              </label>
              <textarea
                value={newGlobalStyle}
                onChange={(e) => setNewGlobalStyle(e.target.value)}
                placeholder={project.globalStyle || "ex: Style aquarelle doux avec des tons pastel..."}
                className="w-full p-3 border rounded-lg font-children"
                rows={2}
              />
              {project.globalStyle && (
                <p className="text-xs text-gray-500 mt-1 font-children">
                  Style actuel: {project.globalStyle.substring(0, 80)}...
                </p>
              )}
            </div>

            {/* Aperçu et modification du prompt */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block font-children font-semibold">
                  Prompt personnalisé (optionnel):
                </label>
                <button
                  onClick={generatePromptPreview}
                  className="btn btn-outline btn-sm"
                  disabled={regenerating}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  Aperçu du prompt
                </button>
              </div>
              <textarea
                value={customPrompt}
                onChange={(e) => setCustomPrompt(e.target.value)}
                placeholder="Laissez vide pour utiliser le prompt automatique généré par l'IA, ou saisissez votre prompt personnalisé..."
                className="w-full p-3 border rounded-lg font-mono text-sm"
                rows={3}
              />
              <p className="text-xs text-gray-500 mt-1 font-children">
                Si rempli, ce prompt sera utilisé à la place du prompt automatique. Utilisez l'aperçu pour voir le prompt qui a été utilisé initialement pour la page sélectionnée.
              </p>
            </div>

            {/* Clé API OpenAI */}
            <div className="mb-6">
              <label className="block font-children font-semibold mb-2">
                Clé API OpenAI (optionnel):
              </label>
              <input
                type="password"
                value={openaiApiKey}
                onChange={(e) => setOpenaiApiKey(e.target.value)}
                placeholder="sk-..."
                className="w-full p-3 border rounded-lg font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1 font-children">
                Laissez vide pour utiliser la clé du projet
              </p>
            </div>

            {/* Nouveaux personnages */}
            <div className="mb-6">
              <div className="flex justify-between items-center mb-2">
                <label className="font-children font-semibold">
                  Nouveaux personnages (optionnel):
                </label>
                <button
                  onClick={addNewCharacter}
                  className="px-3 py-1 bg-green-500 text-white rounded text-sm font-children hover:bg-green-600 transition-colors"
                >
                  + Ajouter
                </button>
              </div>
              {newCharacters.map((char, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="Nom du personnage"
                    value={char.name}
                    onChange={(e) => updateCharacter(index, 'name', e.target.value)}
                    className="flex-1 p-2 border rounded font-children"
                  />
                  <input
                    type="text"
                    placeholder="Description"
                    value={char.description}
                    onChange={(e) => updateCharacter(index, 'description', e.target.value)}
                    className="flex-2 p-2 border rounded font-children"
                  />
                  <button
                    onClick={() => removeCharacter(index)}
                    className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRegenerateModal(false)}
                className="btn btn-secondary"
                disabled={regenerating}
              >
                Annuler
              </button>
              <button
                onClick={handleRegeneratePage}
                disabled={regenerating}
                className="btn btn-primary"
              >
                {regenerating ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    En cours...
                  </>
                ) : (
                  <>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Régénérer cette page
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal d'aperçu du prompt */}
      {showPromptPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-elegant font-bold text-gradient">
                Aperçu du prompt IA
              </h2>
              <button
                onClick={() => {
                  setShowPromptPreview(false);
                  setPreviewPrompt('');
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <div className="mb-4">
              <p className="text-sm text-gray-600 font-children mb-2">
                Voici le prompt qui a été utilisé pour générer l'image de la page {selectedPage} :
              </p>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center mb-2">
                <Sparkles className="h-4 w-4 text-blue-600 mr-2" />
                <h3 className="font-children font-semibold text-blue-800">Prompt utilisé initialement</h3>
              </div>
              <div className="bg-white rounded p-3 border">
                <pre className="font-mono text-xs text-gray-700 leading-relaxed whitespace-pre-wrap">
                  {previewPrompt}
                </pre>
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-500 font-children">
              <p>
                <strong>Note :</strong> Il s'agit du prompt exact qui a été utilisé lors de la génération initiale de cette image. 
                Vous pouvez le copier dans le champ "Prompt personnalisé" ci-dessus si vous souhaitez le modifier pour une régénération.
              </p>
            </div>
            
            <div className="flex gap-3 justify-end mt-6">
              <button
                onClick={() => {
                  setCustomPrompt(previewPrompt);
                  setShowPromptPreview(false);
                  setPreviewPrompt('');
                }}
                className="btn btn-outline"
              >
                Utiliser ce prompt
              </button>
              <button
                onClick={() => {
                  setShowPromptPreview(false);
                  setPreviewPrompt('');
                }}
                className="btn btn-primary"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Modal d'upload d'image */}
      {showUploadModal && projectId && uploadPageNumber && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-lg w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-elegant font-bold text-gradient">
                Upload image - Page {uploadPageNumber}
              </h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadPageNumber(null);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            
            <ImageUpload
              projectId={projectId}
              pageNumber={uploadPageNumber}
              onUploadSuccess={handleUploadSuccess}
              onUploadError={handleUploadError}
              className="mt-4"
            />
          </div>
        </div>
      )}
    </div>
  );
};
